import { useMemo, useEffect, useState } from 'react'

import { Group, Radio, Tooltip, Alert, Text, Select } from '@mantine/core'

import { useMutation, useQuery } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import Empty from 'src/components/Feedback/Empty/Empty'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_DOCUMENT_TEMPLATE_PACKAGES,
  BULK_SIGN_PACKAGE,
} from 'src/graphql/documentTemplatePackages.graphql'
import IconAlertTriangle from 'src/icons/IconAlertTriangle'
import IconDocument from 'src/icons/IconDocument'

const MAX_BULK_SIGN_APPLICANTS = 100

const BulkSignDrawer = ({
  applicants,
  refetchQueries = [],
  onClose,
  setLoading,
  setResults,
}) => {
  const [selectedPackageId, setSelectedPackageId] = useState(null)

  // Calculate applicants without email
  const applicantsWithoutEmail = useMemo(
    () => applicants.filter((a) => !a.email),
    [applicants]
  )

  // QUERIES:
  const { data: documentPackagesList } = useQuery(
    GET_DOCUMENT_TEMPLATE_PACKAGES
  )

  const documentPackages = documentPackagesList?.documentTemplatePackages || []

  // MUTATIONS:
  const [bulkSignPackages, { loading: bulkSignLoading }] = useMutation(
    BULK_SIGN_PACKAGE,
    {
      refetchQueries: refetchQueries,
      onCompleted: (data) => {
        const results = data?.bulkSignPackages?.applicants || []
        const successCount = results.filter((r) => r.success).length
        const failCount = results.filter((r) => !r.success).length

        if (setResults) {
          setResults(
            results.map((r) => ({
              applicantId: r.applicantId,
              success: r.success,
              errorMsg: r.errorMsg || 'Failed to send documents',
            }))
          )
        }

        if (failCount === 0) {
          toast('Document packages sent successfully', 'success')
        } else if (successCount === 0) {
          toast('Failed to send document packages', 'error')
        } else {
          toast(`Sent ${successCount} packages, ${failCount} failed`, 'warning')
        }
      },
      onError: () => {
        if (setResults) {
          setResults(
            applicants.map((a) => ({
              applicantId: a.applicantId,
              success: false,
              errorMsg: 'Failed to send documents',
            }))
          )
        }
        toast('Unable to send document packages, please try again.', 'error')
      },
    }
  )

  useEffect(() => {
    if (setLoading) {
      setLoading(bulkSignLoading)
    }
  }, [bulkSignLoading, setLoading])

  const handleSubmit = () => {
    bulkSignPackages({
      variables: {
        applicants: applicants.map((applicant) => applicant.applicantId),
        documentTemplatePackageId: selectedPackageId,
      },
    })
  }

  const packageOptions = documentPackages.map((pkg) => ({
    value: pkg.documentTemplatePackageId,
    label: pkg.packageName,
  }))

  const selectedPackage = documentPackages.find(
    (pkg) => pkg.documentTemplatePackageId === selectedPackageId
  )

  return (
    <>
      <div className="flex flex-col gap-4">
        {applicants.length > MAX_BULK_SIGN_APPLICANTS && (
          <div className="border-doubleNickel-red-200 bg-doubleNickel-red-50 flex flex-row items-center gap-2 rounded-lg border p-3">
            <IconAlertTriangle className="text-doubleNickel-red-500 h-4 w-4" />
            <Text size="xs" className="text-doubleNickel-red-700">
              Maximum {MAX_BULK_SIGN_APPLICANTS} applicants per batch. Please
              reduce your selection.
            </Text>
          </div>
        )}
        {applicantsWithoutEmail.length > 0 && (
          <div className="border-doubleNickel-yellow-200 bg-doubleNickel-yellow-50 flex flex-row items-center gap-2 rounded-lg border p-3">
            <IconAlertTriangle className="text-doubleNickel-yellow-500 h-4 w-4" />
            <Text size="xs" className="text-doubleNickel-yellow-700">
              {applicantsWithoutEmail.length === 1
                ? `1 applicant (${applicantsWithoutEmail[0].name}) is missing an email and will fail to receive documents.`
                : `${
                    applicantsWithoutEmail.length
                  } applicants are missing emails and will fail to receive documents: ${applicantsWithoutEmail
                    .map((a) => a.name)
                    .join(', ')}`}
            </Text>
          </div>
        )}

        {documentPackages.length > 0 ? (
          <>
            <div className="flex flex-row items-center justify-between">
              <Text size="xs">Document Package</Text>
              <div className="w-[250px]">
                <Select
                  placeholder="Select a package"
                  data={packageOptions}
                  value={selectedPackageId}
                  onChange={setSelectedPackageId}
                  searchable
                  clearable
                  nothingFoundMessage="No packages found"
                  checkIconPosition="right"
                  size="xs"
                  classNames={{
                    input: 'text-xs text-right text-doubleNickel-gray-900',
                    dropdown: 'border-doubleNickel-gray-200',
                    option:
                      'text-xs text-doubleNickel-gray-900 data-[combobox-selected]:bg-transparent data-[combobox-active]:bg-transparent hover:bg-doubleNickel-gray-100',
                  }}
                />
              </div>
            </div>

          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-6">
            <Empty
              title="No document packages found"
              subtitle="Please contact Double Nickel to set up document packages for your account."
            />
          </div>
        )}
      </div>

      <Group className="mt-auto grid grid-cols-2">
        <Button
          text="Cancel"
          variant="outline"
          onClick={onClose}
          disabled={bulkSignLoading}
        />
        <Tooltip
          disabled={
            selectedPackageId &&
            applicants.length > 0 &&
            applicants.length <= MAX_BULK_SIGN_APPLICANTS
          }
          label={
            applicants.length > MAX_BULK_SIGN_APPLICANTS
              ? `Maximum ${MAX_BULK_SIGN_APPLICANTS} applicants allowed`
              : 'Please select a document package'
          }
        >
          <Button
            text={'Send for Signing'}
            variant="filled"
            lefticon={<IconDocument />}
            disabled={
              !selectedPackageId ||
              applicants.length === 0 ||
              applicants.length > MAX_BULK_SIGN_APPLICANTS
            }
            loading={bulkSignLoading}
            onClick={() => {
              handleSubmit()
            }}
          />
        </Tooltip>
      </Group>
    </>
  )
}

export default BulkSignDrawer
